import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, ScrollText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { platformApi } from '../api'

/** Human wording for the action codes the backend records. */
const ACTIONS: Record<string, string> = {
  'platform.login': 'Operator signed in',
  'platform.login_failed': 'Sign-in refused',
  'platform.account_locked': 'Operator account locked',
  'platform.password_changed': 'Operator changed their password',
  'platform.trial_ended': 'Trial ended',
  'platform.bootstrap': 'First operator created',
  'platform.operator_created': 'Operator invited',
  'platform.operator_role_changed': 'Operator role changed',
  'platform.operator_status_changed': 'Operator enabled or disabled',
  'platform.operator_password_set': 'Operator password set',
  'platform.plan_created': 'Plan created',
  'platform.plan_updated': 'Plan changed',
  'platform.plan_repriced': 'Plan repriced',
  'platform.plan_offered': 'Plan offered',
  'platform.plan_withdrawn': 'Plan withdrawn',
  'platform.plan_deleted': 'Plan deleted',
  'platform.billing_run': 'Billing run',
  'platform.invoice_issued': 'Invoice issued',
  'platform.invoice_paid': 'Invoice paid',
  'platform.invoice_voided': 'Invoice voided',
  'tenant.provision': 'Workspace created',
  'tenant.suspend': 'Workspace suspended',
  'tenant.resume': 'Workspace resumed',
  'tenant.cancel': 'Workspace cancelled',
}

/** Everything operators have done to this installation. */
export function PlatformAuditPage() {
  const [page, setPage] = useState(0)
  const [filter, setFilter] = useState('')

  const trail = useQuery({
    queryKey: ['platform', 'audit', page],
    queryFn: () => platformApi.audit(undefined, page, 50),
    placeholderData: keepPreviousData,
  })

  const term = filter.trim().toLowerCase()
  const rows = (trail.data?.content ?? []).filter(
    (row) =>
      term === '' ||
      row.actorEmail.toLowerCase().includes(term) ||
      row.action.toLowerCase().includes(term) ||
      (row.tenantName ?? '').toLowerCase().includes(term),
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-heading)]">Audit trail</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Every operator action on this installation, newest first.
          </p>
        </div>
        <Input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter by operator, action or workspace"
          aria-label="Filter the audit trail"
          className="ms-auto w-72"
        />
      </div>

      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
        <table className="w-full min-w-[48rem] text-sm">
          <thead className="bg-[var(--color-page)] text-xs uppercase text-[var(--color-muted)]">
            <tr>
              <th className="px-4 py-3 text-start font-medium">When</th>
              <th className="px-4 py-3 text-start font-medium">Operator</th>
              <th className="px-4 py-3 text-start font-medium">Action</th>
              <th className="px-4 py-3 text-start font-medium">Workspace</th>
              <th className="px-4 py-3 text-start font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {trail.isPending && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[var(--color-muted)]">
                  Loading…
                </td>
              </tr>
            )}

            {!trail.isPending && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center">
                  <ScrollText className="mx-auto mb-2 size-6 text-[var(--color-muted)]" aria-hidden />
                  <p className="text-sm text-[var(--color-muted)]">Nothing recorded yet.</p>
                </td>
              </tr>
            )}

            {rows.map((entry) => (
              <tr key={entry.id} className="border-t border-[var(--color-border)] align-top">
                <td className="whitespace-nowrap px-4 py-3 text-[var(--color-muted)]">
                  {new Date(entry.occurredAt).toLocaleString()}
                </td>
                <td className="px-4 py-3">{entry.actorEmail}</td>
                <td className="px-4 py-3 font-medium text-[var(--color-heading)]">
                  {ACTIONS[entry.action] ?? entry.action}
                </td>
                <td className="px-4 py-3">{entry.tenantName ?? '—'}</td>
                <td className="px-4 py-3">
                  {Object.keys(entry.detail).length === 0 ? (
                    <span className="text-[var(--color-muted)]">—</span>
                  ) : (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-2 text-xs">
                      {Object.entries(entry.detail).map(([key, value]) => (
                        <div key={key} className="contents">
                          <dt className="text-[var(--color-muted)]">{key}</dt>
                          <dd className="text-[var(--color-body)]">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-[var(--color-muted)]">
        <span>
          {trail.data
            ? `Page ${trail.data.page + 1} of ${Math.max(trail.data.totalPages, 1)} · ${trail.data.totalElements} entries`
            : ''}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={page === 0}
            onClick={() => setPage((current) => Math.max(current - 1, 0))}
          >
            <ChevronLeft className="me-1 size-4" aria-hidden />
            Newer
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={!trail.data || page + 1 >= trail.data.totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            Older
            <ChevronRight className="ms-1 size-4" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  )
}
